// The mandatory-upload dropzone (§5.1). Parses via the worker, streams progress,
// and flips into a receipt on success. Save gate stays disabled until parse.

import { useCallback, useRef, useState } from "react";
import type { DistilledProfile } from "../lib/types.ts";
import { parseExportFile, ExportRejectedError } from "../lib/useParser.ts";

interface Props {
  onParsed: (profile: DistilledProfile) => void;
  compact?: boolean;
}

type State =
  | { kind: "idle" }
  | { kind: "parsing"; note: string }
  | { kind: "done"; profile: DistilledProfile }
  | { kind: "error"; message: string };

export function Dropzone({ onParsed, compact }: Props) {
  const [state, setState] = useState<State>({ kind: "idle" });
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFile = useCallback(
    async (file: File) => {
      setState({ kind: "parsing", note: "Reading export…" });
      try {
        const profile = await parseExportFile(file, (name, rows) => {
          setState({ kind: "parsing", note: `Reading ${name.replace(".csv", "")}… ${rows}` });
        });
        setState({ kind: "done", profile });
        onParsed(profile);
      } catch (err) {
        const message =
          err instanceof ExportRejectedError
            ? "That doesn't look like a Letterboxd export. Re-export from letterboxd.com/settings/data and pick the .zip."
            : `Couldn't read that file: ${(err as Error).message}`;
        setState({ kind: "error", message });
      }
    },
    [onParsed],
  );

  const onDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) void handleFile(file);
  };

  if (state.kind === "done") {
    const c = state.profile.counts;
    const avg = averageRating(state.profile);
    return (
      <div data-testid="onboarding-receipt" className="card border-green-500/40">
        <div className="flex items-center gap-2 text-green-500">
          <CheckIcon />
          <span className="font-medium">Export read</span>
        </div>
        <p className="mt-3 text-lg text-ink-900">
          <strong className="tabular-nums">{c.watched.toLocaleString()}</strong> films ·{" "}
          <strong className="tabular-nums">{c.rated.toLocaleString()}</strong> rated ·{" "}
          <strong>★{avg.toFixed(1)}</strong> average · watchlist of{" "}
          <strong className="tabular-nums">{c.watchlist.toLocaleString()}</strong>
        </p>
        <button
          type="button"
          className="mt-4 text-sm text-ink-400 underline"
          onClick={() => {
            setState({ kind: "idle" });
            inputRef.current?.click();
          }}
        >
          Pick a different export
        </button>
      </div>
    );
  }

  return (
    <div>
      <label
        data-testid="action-dropzone"
        onDragOver={(e) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        className={`flex ${compact ? "min-h-[120px]" : "min-h-[180px]"} cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed p-6 text-center transition-colors ${
          dragging ? "border-gold-500 bg-paper-100" : "border-border-strong bg-card hover:border-border-strong"
        } ${state.kind === "error" ? "border-red-600/60" : ""}`}
      >
        <input
          ref={inputRef}
          data-testid="action-file-input"
          type="file"
          accept=".zip"
          className="sr-only"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void handleFile(file);
          }}
        />
        {state.kind === "parsing" ? (
          <p className="text-ink-900">{state.note}</p>
        ) : (
          <>
            <UploadIcon />
            <p className="mt-2 font-medium text-ink-900">Drop your Letterboxd export .zip</p>
            <p className="mt-1 text-sm text-ink-400">or tap to choose a file</p>
          </>
        )}
      </label>
      {state.kind === "error" && (
        <p data-testid="onboarding-error" className="mt-2 text-sm text-red-600">
          {state.message}
        </p>
      )}
      <p className="mt-3 text-xs text-ink-400">
        On your phone? Download the export in your browser, then pick the zip from Files/Downloads. Get it at{" "}
        <a href="https://letterboxd.com/data/export/" target="_blank" rel="noopener noreferrer">
          letterboxd.com/data/export
        </a>
        .
      </p>
    </div>
  );
}

function averageRating(p: DistilledProfile): number {
  let sum = 0;
  let n = 0;
  for (const [rating, count] of Object.entries(p.aggregates.ratingHist)) {
    sum += parseFloat(rating) * count;
    n += count;
  }
  return n === 0 ? 0 : sum / n;
}

const UploadIcon = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" className="text-ink-400">
    <path d="M12 16V4m0 0L8 8m4-4l4 4" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M4 16v2a2 2 0 002 2h12a2 2 0 002-2v-2" strokeLinecap="round" />
  </svg>
);
const CheckIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M20 6L9 17l-5-5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
